import classnames from 'classnames';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as api from '../../api';

import LabeledInput from '../LabeledInput';
import { SelectInput } from '../SelectInput';
import { TextInput } from '../TextInput';

import styles from './index.module.css';

const NUMBERS_ONLY_REGEX = /^\d*$/;

interface Props {
  value: Partial<api.PrevisitPatientGrossIncome.GrossIncomeFormFieldValue>;
  updateFieldValue: (value: string) => void;
  saveFieldValue: (value: string) => void;
}

const INCOME_PERIOD_STRINGS: Record<
  api.PrevisitPatientGrossIncome.IncomePeriod,
  string
> = {
  [api.PrevisitPatientGrossIncome.IncomePeriod.Annually]: 'Annually',
  [api.PrevisitPatientGrossIncome.IncomePeriod.BiWeekly]: 'Bi-weekly',
  [api.PrevisitPatientGrossIncome.IncomePeriod.Monthly]: 'Monthly',
};

const INCOME_PERIOD_MULTIPLIERS: {
  [key in api.PrevisitPatientGrossIncome.IncomePeriod]: number;
} = {
  [api.PrevisitPatientGrossIncome.IncomePeriod.Annually]: 1,
  [api.PrevisitPatientGrossIncome.IncomePeriod.BiWeekly]: 26,
  [api.PrevisitPatientGrossIncome.IncomePeriod.Monthly]: 12,
};

const incomePeriodOptions = Object.values(
  api.PrevisitPatientGrossIncome.IncomePeriod,
).map((period) => ({
  display:
    INCOME_PERIOD_STRINGS[period] ??
    `${period[0].toUpperCase()}${period.slice(1).toLowerCase()}`,
  key: period,
  value: period,
}));

const GrossIncome: React.FC<Props> = ({
  value,
  updateFieldValue,
  saveFieldValue,
}) => {
  const { t } = useTranslation();
  const [income, setIncome] = useState<number | undefined>(value?.income);
  const [incomePeriod, setIncomePeriod] =
    useState<api.PrevisitPatientGrossIncome.IncomePeriod>(
      value?.incomePeriod ??
        api.PrevisitPatientGrossIncome.IncomePeriod.Annually,
    );

  let incomeTitleDisplay = null;
  if (
    income !== undefined &&
    incomePeriod !== api.PrevisitPatientGrossIncome.IncomePeriod.Annually
  ) {
    const formattedIncomePeriod = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'usd',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(income * INCOME_PERIOD_MULTIPLIERS[incomePeriod]);

    incomeTitleDisplay = t('incomePeriodAnnually', {
      income: formattedIncomePeriod,
    });
  }

  const saveFormField = () => {
    const newValue = JSON.stringify({ income, incomePeriod });
    updateFieldValue(newValue);
    saveFieldValue(newValue);
  };
  return (
    <>
      <LabeledInput label={t('grossHouseholdIncome')} htmlFor="gross-income">
        <div className={styles.CombinedInputRow} onBlur={saveFormField}>
          <TextInput
            id="income-amount"
            className={classnames(styles.DollarRow, styles.InputPosition)}
            value={income === undefined ? '' : income}
            type="number"
            onChange={(event) => {
              const valid =
                NUMBERS_ONLY_REGEX.exec(event.currentTarget.value) !== null;
              if (!valid) return;
              const numberValue = parseInt(event.currentTarget.value);
              setIncome(isNaN(numberValue) ? undefined : numberValue);
            }}
          />
          <SelectInput
            id="income-period-select"
            value={incomePeriod}
            options={incomePeriodOptions}
            onChange={(newValue) => setIncomePeriod(newValue)}
          />
        </div>
      </LabeledInput>
      {incomeTitleDisplay && (
        <div className={styles.Subtitle}>{incomeTitleDisplay}</div>
      )}
    </>
  );
};

export default GrossIncome;
